import React from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorState } from './EmptyState';

/**
 * Frontiere d'erreur — une page qui tombe ne doit pas emporter l'application.
 *
 * Motif : le 21/08/2026, une variable hors portee dans `RiskAlertsGated` a leve
 * une ReferenceError au rendu. React, sans frontiere, demonte alors la racine :
 * plus de barre laterale, plus de routeur, un ecran blanc d'ou l'on ne peut
 * meme pas revenir en arriere — c'est mot pour mot ce qu'a decrit le client.
 * Avec cette frontiere, le meme defaut degrade UNE page et laisse la navigation
 * intacte.
 *
 * Elle ne remplace pas les gardes en amont (src/lintUndef.test.js, la spec 59) :
 * elle limite les degats de ce qu'elles n'auront pas vu.
 *
 * DEUX POSES, DEUX MESSAGES. Sous ProtectedLayout la barre laterale survit et
 * l'utilisateur peut aller ailleurs ; au niveau de l'application, c'est `Routes`
 * qui a disparu — il n'y a plus AUCUN lien a cliquer, et lui conseiller le menu
 * serait un mensonge. L'appelant fournit donc son message et son recours.
 *
 * REARMEMENT — une frontiere ne se reinitialise jamais d'elle-meme : une fois
 * declenchee, elle afficherait son erreur sur toutes les pages suivantes. On la
 * rearme via `resetKey` (le chemin courant) et NON via `key` : une `key` qui
 * change a chaque navigation remonterait tout le sous-arbre a chaque clic,
 * detruisant le fondu et l'etat des pages (la FilterBar de /results, entre
 * autres, doit survivre au passage d'un onglet a l'autre).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { enErreur: false };
  }

  static getDerivedStateFromError() {
    return { enErreur: true };
  }

  componentDidCatch(error, info) {
    // Pas de collecte cote client : la console reste le seul endroit ou
    // l'exception d'origine survit, et on la veut entiere pour le diagnostic.
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.enErreur && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ enErreur: false });
    }
  }

  render() {
    if (!this.state.enErreur) return this.props.children;

    return (
      <div className="py-10">
        <ErrorState
          message={
            this.props.message ||
            "Cette page n'a pas pu s'afficher. Les autres restent accessibles depuis le menu."
          }
          onRetry={this.props.onRetry || (() => this.setState({ enErreur: false }))}
        />
      </div>
    );
  }
}

/** Frontiere rearmee a chaque changement de route. A utiliser sous un Router. */
export function RouteErrorBoundary({ children, message, onRetry }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKey={pathname} message={message} onRetry={onRetry}>
      {children}
    </ErrorBoundary>
  );
}
